import { ethers } from 'ethers';
import transactionService from './transactionService';

/**
 * Service for interacting with the Emission Controller contract
 */
class EmissionControllerService {
  /**
   * Create a new EmissionControllerService instance
   * @param {Object} contract - Ethers.js contract instance
   */
  constructor(contract) {
    this.contract = contract;
  }

  /**
   * Get current epoch information
   * @returns {Promise<Object>} - Current epoch information
   */
  async getCurrentEpochInfo() {
    try {
      const result = await this.contract.getCurrentEpochInfo();
      
      return {
        currentEpochNumber: result.currentEpochNumber.toNumber(),
        isActive: result.isActive,
        startTime: result.startTime.toNumber(),
        endTime: result.endTime.toNumber()
      };
    } catch (error) {
      console.error('Error getting current epoch info:', error);
      throw error;
    }
  }

  /**
   * Force stop the current epoch (debugging only)
   * @returns {Promise<Object>} - Transaction result
   */
  async forceStopEpoch() {
    try {
      const tx = this.contract.forceStopEpoch();
      return await transactionService.sendTransaction(tx, {
        title: 'Force Stop Epoch',
        description: 'Force stopping the current epoch for debugging purposes',
        type: 'force_stop_epoch'
      });
    } catch (error) {
      console.error('Error force stopping epoch:', error);
      throw error;
    }
  }

  /**
   * Get total emissions for a specific epoch
   * @param {number} epochNumber - Epoch number
   * @returns {Promise<Object>} - Total emissions for the epoch
   */
  async getEpochTotalEmissions(epochNumber) {
    try {
      const emissions = await this.contract.epochTotalEmissions(epochNumber);
      return {
        value: emissions,
        formatted: ethers.utils.formatEther(emissions)
      };
    } catch (error) {
      console.error(`Error getting total emissions for epoch ${epochNumber}:`, error);
      throw error;
    }
  }

  /**
   * Get project's share of emissions for a specific epoch
   * @param {number} epochNumber - Epoch number
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - Project emissions for the epoch
   */
  async getEpochProjectEmissions(epochNumber, projectId) {
    try {
      const emissions = await this.contract.epochProjectEmissions(epochNumber, projectId);
      return {
        value: emissions,
        formatted: ethers.utils.formatEther(emissions)
      };
    } catch (error) {
      console.error(`Error getting project emissions for epoch ${epochNumber} and project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * Get project's IU holder emissions for a specific epoch
   * @param {number} epochNumber - Epoch number
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - IU holder emissions for the epoch
   */
  async getEpochProjectIUHolderEmissions(epochNumber, projectId) {
    try {
      const emissions = await this.contract.epochProjectIUHolderEmissions(epochNumber, projectId);
      return {
        value: emissions,
        formatted: ethers.utils.formatEther(emissions)
      };
    } catch (error) {
      console.error(`Error getting IU holder emissions for epoch ${epochNumber} and project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * Get project's IU holder emissions for a specific epoch
   * @param {number} epochNumber - Epoch number
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - IU holder emissions for the epoch
   */
  async getEpochProjectTreasuryEmissions(epochNumber, projectId) {
    try {
      const emissions = await this.contract.epochProjectTreasuryEmissions(epochNumber, projectId);
      return {
        value: emissions,
        formatted: ethers.utils.formatEther(emissions)
      };
    } catch (error) {
      console.error(`Error getting treasuryEmissions emissions for epoch ${epochNumber} and project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * Get project's staking emissions for a specific epoch
   * @param {number} epochNumber - Epoch number
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - Staking emissions for the epoch
   */
  async getEpochProjectStakingEmissions(epochNumber, projectId) {
    try {
      const emissions = await this.contract.epochProjectStakingEmissions(epochNumber, projectId);
      return {
        value: emissions,
        formatted: ethers.utils.formatEther(emissions)
      };
    } catch (error) {
      console.error(`Error getting staking emissions for epoch ${epochNumber} and project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * Get all emissions data for a specific epoch and project
   * @param {number} epochNumber - Epoch number
   * @param {number} projectId - Project ID
   * @returns {Promise<Object>} - All emissions data for the epoch and project
   */
  async getAllEpochProjectEmissions(epochNumber, projectId) {
    try {
      const [totalEmissions, projectEmissions, iuHolderEmissions, treasuryEmissions, stakingEmissions] = await Promise.all([
        this.getEpochTotalEmissions(epochNumber),
        this.getEpochProjectEmissions(epochNumber, projectId),
        this.getEpochProjectIUHolderEmissions(epochNumber, projectId),
        this.getEpochProjectTreasuryEmissions(epochNumber, projectId),
        this.getEpochProjectStakingEmissions(epochNumber, projectId)
      ]);
      
      // Calculate project share percentage
      const projectSharePercentage = totalEmissions.value.gt(0) 
        ? projectEmissions.value.mul(10000).div(totalEmissions.value).toNumber() / 100
        : 0;
      
      // Calculate IU holder share percentage of project emissions
      const iuHolderSharePercentage = projectEmissions.value.gt(0)
        ? iuHolderEmissions.value.mul(10000).div(projectEmissions.value).toNumber() / 100
        : 0;
      
      // Calculate staking share percentage of project emissions
      const stakingSharePercentage = projectEmissions.value.gt(0)
        ? stakingEmissions.value.mul(10000).div(projectEmissions.value).toNumber() / 100
        : 0;

      // Calculate treasury share percentage of project emissions
      const treasurySharePercentage = projectEmissions.value.gt(0)
        ? treasuryEmissions.value.mul(10000).div(projectEmissions.value).toNumber() / 100
        : 0;
      
      return {
        epochNumber,
        totalEmissions,
        projectEmissions,
        iuHolderEmissions,
        stakingEmissions,
        treasuryEmissions,
        projectSharePercentage,
        iuHolderSharePercentage,
        stakingSharePercentage,
        treasurySharePercentage
      };
    } catch (error) {
      console.error(`Error getting all emissions data for epoch ${epochNumber} and project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * Get historical emissions data for all epochs for a specific project
   * @param {number} projectId - Project ID
   * @param {number} [maxEpochs=10] - Maximum number of epochs to fetch (defaults to 10)
   * @returns {Promise<Array>} - Array of emissions data for each epoch
   */
  async getHistoricalProjectEmissions(projectId, maxEpochs = 10) {
    try {
      const currentEpochInfo = await this.getCurrentEpochInfo();
      const currentEpochNumber = currentEpochInfo.currentEpochNumber;
      
      // Determine how many epochs to fetch
      const startEpoch = Math.max(1, currentEpochNumber - maxEpochs + 1);
      const epochsToFetch = [];
      
      for (let i = startEpoch; i <= currentEpochNumber; i++) {
        epochsToFetch.push(i);
      }
      
      // Fetch emissions data for all epochs in parallel
      const emissionsData = await Promise.all(
        epochsToFetch.map(epochNumber => 
          this.getAllEpochProjectEmissions(epochNumber, projectId)
        )
      );
      
      return emissionsData.reverse(); // Return with most recent epoch first
    } catch (error) {
      console.error(`Error getting historical emissions for project ${projectId}:`, error);
      throw error;
    }
  }

  /**
   * Check for unclaimed staking emissions
   * @param {number} epochNumber - Epoch number
   * @param {number} projectId - Project ID
   * @param {string} userAddress - User wallet address
   * @returns {Promise<Object>} - Unclaimed emissions info
   */
  async checkUnclaimedStakingEmissions(epochNumber, projectId, userAddress) {
    try {
      const result = await this.contract.checkUnclaimedStakingEmissions(
        epochNumber,
        projectId,
        userAddress || ethers.constants.AddressZero
      );
      
      return {
        hasUnclaimed: result.hasUnclaimed,
        amount: result.amount,
        formattedAmount: ethers.utils.formatEther(result.amount)
      };
    } catch (error) {
      console.error('Error checking unclaimed staking emissions:', error);
      throw error;
    }
  }

  /**
   * Check for unclaimed IU holder emissions
   * @param {number} epochNumber - Epoch number
   * @param {number} projectId - Project ID
   * @param {string} userAddress - User wallet address
   * @returns {Promise<Object>} - Unclaimed emissions info
   */
  async checkUnclaimedIUHolderEmissions(epochNumber, projectId, userAddress) {
    try {
      const result = await this.contract.checkUnclaimedIUHolderEmissions(
        epochNumber,
        projectId,
        userAddress || ethers.constants.AddressZero
      );
      
      return {
        hasUnclaimed: result.hasUnclaimed,
        amount: result.amount,
        formattedAmount: ethers.utils.formatEther(result.amount)
      };
    } catch (error) {
      console.error('Error checking unclaimed IU holder emissions:', error);
      throw error;
    }
  }

  /**
   * Claim staking emissions for a specific epoch and project
   * @param {number} epochNumber - Epoch number to claim for
   * @param {number} projectId - Project ID to claim for
   * @returns {Promise<Object>} - Transaction result
   */
  async claimStakingEmissions(epochNumber, projectId) {
    try {
      const tx = this.contract.claimStakingEmissions(epochNumber, projectId);
      return await transactionService.sendTransaction(tx, {
        title: 'Claim Staking Emissions',
        description: `Claiming staking emissions for project #${projectId} from epoch ${epochNumber}`,
        type: 'claim_staking_emissions'
      });
    } catch (error) {
      console.error('Error claiming staking emissions:', error);
      throw error;
    }
  }

  /**
   * Claim IU holder emissions for a specific epoch and project ///
   * @param {number} epochNumber - Epoch number to claim for
   * @param {number} projectId - Project ID to claim for
   * @returns {Promise<Object>} - Transaction result
   */
  async claimIUHolderEmissions(epochNumber, projectId) {
    try {
      const tx = this.contract.claimIUHolderEmissions(epochNumber, projectId);
      return await transactionService.sendTransaction(tx, {
        title: 'Claim IU Holder Emissions',
        description: `Claiming IU holder emissions for project #${projectId} from epoch ${epochNumber}`,
        type: 'claim_iu_holder_emissions'
      });
    } catch (error) {
      console.error('Error claiming IU holder emissions:', error);
      throw error;
    }
  }   



  /**
   * Get epoch duration
   * @returns {Promise<number>} - Epoch duration in seconds
   */
  async getEpochDuration() {
    try {
      const duration = await this.contract.epochDuration();
      return duration.toNumber();
    } catch (error) {
      console.error('Error getting epoch duration:', error);
      throw error;
    }
  }
  
  /**
   * Get epoch duration in days
   * @returns {Promise<number>} - Epoch duration in days
   */
  async getEpochDurationInDays() {
    try {
      const durationInSeconds = await this.getEpochDuration();
      return Math.floor(durationInSeconds / (24 * 60 * 60));
    } catch (error) {
      console.error('Error getting epoch duration in days:', error);
      throw error;
    }
  }
  
  /**
   * Get base emissions per epoch
   * @returns {Promise<Object>} - Base emissions amount with formatted value
   */
  async getBaseEmissionsPerEpoch() {
    try {
      const emissions = await this.contract.baseEmissionsPerEpoch();
      return {
        value: emissions,
        formatted: ethers.utils.formatEther(emissions)
      };
    } catch (error) {
      console.error('Error getting base emissions per epoch:', error);
      throw error;
    }
  }
  
  /**
   * Get maximum emissions per epoch
   * @returns {Promise<Object>} - Maximum emissions amount with formatted value
   */
  async getMaxEmissionsPerEpoch() {
    try {
      const emissions = await this.contract.maxEmissionsPerEpoch();
      return {
        value: emissions,
        formatted: ethers.utils.formatEther(emissions)
      };
    } catch (error) {
      console.error('Error getting maximum emissions per epoch:', error);
      throw error;
    }
  }
  
  /**
   * Format time until next epoch
   * @returns {Promise<string>} - Formatted time string
   */
  async formatTimeUntilNextEpoch() {
    try {
      const epochInfo = await this.getCurrentEpochInfo();
      
      if (!epochInfo.isActive) {
        return 'No active epoch';
      }
      
      const now = Math.floor(Date.now() / 1000);
      const timeRemaining = epochInfo.endTime - now;
      
      if (timeRemaining <= 0) {
        return 'Epoch ended';
      }
      
      const days = Math.floor(timeRemaining / 86400);
      const hours = Math.floor((timeRemaining % 86400) / 3600);
      const minutes = Math.floor((timeRemaining % 3600) / 60);
      
      if (days > 0) {
        return `${days}d ${hours}h ${minutes}m`;
      } else if (hours > 0) {
        return `${hours}h ${minutes}m`;
      } else {
        return `${minutes}m`;
      }
    } catch (error) {
      console.error('Error formatting time until next epoch:', error);
      throw error;
    }
  }
  
  /**
   * Format epoch time range
   * @param {number} startTimestamp - Start timestamp
   * @param {number} endTimestamp - End timestamp
   * @returns {string} - Formatted time range
   */
  formatEpochTimeRange(startTimestamp, endTimestamp) {
    const startDate = new Date(startTimestamp * 1000);
    const endDate = new Date(endTimestamp * 1000);
    
    const formatDate = (date) => {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    };
    
    return `${formatDate(startDate)} - ${formatDate(endDate)}`;
  }
  
  /**
   * Get epoch status
   * @param {number} startTimestamp - Start timestamp
   * @param {number} endTimestamp - End timestamp
   * @param {boolean} isActive - Whether the epoch is active
   * @returns {string} - Status (Active, Ended, Pending)
   */
  getEpochStatus(startTimestamp, endTimestamp, isActive) {
    const now = Math.floor(Date.now() / 1000);
    
    if (!isActive) {
      return 'Pending';
    } else if (now < endTimestamp) {
      return 'Active';
    } else {
      return 'Ended';
    }
  }

  /**
   * Get global emissions history for all epochs
   * @returns {Promise<Array>} - Array of emissions data for all epochs
   */
  async getGlobalEmissionsHistory() {
    try {
      // Get current epoch number from contract
      const epochInfo = await this.getCurrentEpochInfo();
      const currentEpochNumber = epochInfo.currentEpochNumber;
      
      if (currentEpochNumber === 0) {
        return []; // No epochs have been processed yet
      }
      
      // Create an array to store all epoch emissions data
      const epochsData = [];
      
      // Fetch data for all epochs from 1 to currentEpoch
      for (let epochNumber = 1; epochNumber <= currentEpochNumber; epochNumber++) {
        try {
          // Get total emissions for this epoch
          const totalEmissions = await this.getEpochTotalEmissions(epochNumber);
          
          // Initialize aggregated values for IU holders, treasury, and staking across all projects
          let totalIUHolderEmissions = ethers.BigNumber.from(0);
          let totalTreasuryEmissions = ethers.BigNumber.from(0);
          let totalStakingEmissions = ethers.BigNumber.from(0);
          
          // Fetch project-specific emission data
          // Note: In a real implementation, we would need to iterate through all projects
          // For simplicity and performance, we'll estimate values based on total emissions
          
          try {
            // Try to get the distribution directly from contract for available epochs
            const iuHolderEmissions = await this.contract.epochIUHolderEmissions(epochNumber);
            const treasuryEmissions = await this.contract.epochTreasuryEmissions(epochNumber);
            const stakingEmissions = await this.contract.epochStakingEmissions(epochNumber);
            
            totalIUHolderEmissions = iuHolderEmissions;
            totalTreasuryEmissions = treasuryEmissions;
            totalStakingEmissions = stakingEmissions;
          } catch (error) {
            // If direct values aren't available, estimate based on total emissions
            // These percentages should match your contract's distribution logic
            const iuHolderShare = 3000; // 30%
            const treasuryShare = 2000; // 20%
            const stakingShare = 5000; // 50%
            
            totalIUHolderEmissions = totalEmissions.value.mul(iuHolderShare).div(10000);
            totalTreasuryEmissions = totalEmissions.value.mul(treasuryShare).div(10000);
            totalStakingEmissions = totalEmissions.value.mul(stakingShare).div(10000);
          }
          
          // Add epoch data to the array
          epochsData.push({
            epochNumber,
            totalEmissions,
            iuHolderEmissions: {
              value: totalIUHolderEmissions,
              formatted: ethers.utils.formatEther(totalIUHolderEmissions)
            },
            treasuryEmissions: {
              value: totalTreasuryEmissions,
              formatted: ethers.utils.formatEther(totalTreasuryEmissions)
            },
            stakingEmissions: {
              value: totalStakingEmissions,
              formatted: ethers.utils.formatEther(totalStakingEmissions)
            },
            // Calculate percentages
            iuHolderPercentage: totalEmissions.value.gt(0) 
              ? totalIUHolderEmissions.mul(10000).div(totalEmissions.value).toNumber() / 100
              : 0,
            treasuryPercentage: totalEmissions.value.gt(0)
              ? totalTreasuryEmissions.mul(10000).div(totalEmissions.value).toNumber() / 100
              : 0,
            stakingPercentage: totalEmissions.value.gt(0)
              ? totalStakingEmissions.mul(10000).div(totalEmissions.value).toNumber() / 100
              : 0
          });
        } catch (error) {
          console.error(`Error getting data for epoch ${epochNumber}:`, error);
          continue; // Skip this epoch and continue with others
        }
      }
      
      // Sort epochs in descending order (newest first)
      return epochsData.sort((a, b) => b.epochNumber - a.epochNumber);
    } catch (error) {
      console.error('Error getting global emissions history:', error);
      throw error;
    }
  }
}

export default EmissionControllerService; 